<template>
    <button
        type="button"
        class="menu-item"
        :class="componentClasses"
        @click="navigate"
        @keydown.enter="() => navigate"
    >
        <flex-container align-items="center">
            <text-decorator :weight="isActive ? 'bold' : 'medium'">
                {{ title }}
            </text-decorator>
            <chip-informative v-if="chipTitle" :color="chipColor">
                {{ chipTitle }}
            </chip-informative>
        </flex-container>
    </button>
</template>

<script setup lang="ts">
    import { Color, TextDecorator, ChipInformative, type ColorType } from '@keyvue/one-ui';
    import { computed } from 'vue';
    import { type RouterLinkProps, useLink } from 'vue-router';

    export interface Props extends RouterLinkProps {
        title: string,
        hideActiveState?: boolean
        chipTitle?: string,
        chipColor?: Color | ColorType,
    }

    const props = defineProps<Props>();
    const { navigate, isActive } = useLink(props);

    const componentClasses = computed(() => ({
        'menu-item--selected': isActive.value,
        'menu-item--not-selected': !isActive.value,
        'menu-item--hide-active-state': props.hideActiveState,
    }));
</script>

<style scoped lang="scss">
.menu-item {
    width: calc(100% - size(xl));
    padding: size(sm);
    color: color(gray-800);
    background-color: transparent;
    transition: background-color 0.1s ease;
    border-radius: size(xs);
    cursor: pointer;
    user-select: none;
    border:0;
    position: relative;

    &:before {
        inset: 0;
        content: '';
        position: absolute;
        border-radius: size(xs);
        background-color: transparent;
        transition: background-color 0.1s ease;
        opacity: .15;
    }

    &--selected:not(.menu-item--hide-active-state) {
        background-color: white;

        &:before {
            background-color: var(--theme-colors-default--primary);
        }
    }

    &--selected {
        color: var(--theme-colors-default--primary);
    }

    &--not-selected {
        &:hover {
            background-color: white;

            &:before {
                background-color: var(--theme-colors-default--primary);
            }
        }
    }

    &:disabled {
        color: lightgray;
    }
}
</style>
